import React, { useState, useEffect } from 'react';
import { X, Lock, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';

interface MicrosoftLoginModalProps {
    isOpen: boolean;
    onClose: () => void;
    onLoginSuccess: (email: string) => void;
}

const MicrosoftLoginModal: React.FC<MicrosoftLoginModalProps> = ({ isOpen, onClose, onLoginSuccess }) => {
    const [step, setStep] = useState<'email' | 'password'>('email');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);

    // Reset state when modal opens
    useEffect(() => {
        if (isOpen) {
            setStep('email');
            setEmail('');
            setPassword('');
            setLoading(false);
        }
    }, [isOpen]);

    const handleNext = (e: React.FormEvent) => {
        e.preventDefault();
        if (email) {
            setLoading(true);
            setTimeout(() => {
                setLoading(false);
                setStep('password');
            }, 800);
        }
    };

    const handleLogin = (e: React.FormEvent) => {
        e.preventDefault();
        if (password) {
            setLoading(true);
            setTimeout(() => {
                setLoading(false);
                onLoginSuccess(email);
                onClose();
            }, 1000);
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent showCloseButton={false} className="p-0 border-none ring-0 bg-transparent shadow-none max-w-[440px] gap-0 w-[calc(100%-2rem)] sm:max-w-[440px]">
                <DialogTitle className="sr-only">Sign in with Microsoft</DialogTitle>
                <div className="w-full bg-white rounded-xl shadow-[0_20px_60px_-15px_rgba(0,0,0,0.5)] overflow-hidden flex flex-col font-sans">
                    {/* Browser-like Header */}
                    <div className="bg-[#f3f3f3] border-b border-gray-300 px-3 py-2.5 flex items-center gap-3">
                        <div className="flex-1 bg-white rounded-md border border-gray-300 px-3 py-1 flex items-center gap-2 text-[11px] text-gray-600 font-mono shadow-sm overflow-hidden">
                            <Lock className="w-3 h-3 text-green-600 shrink-0" />
                            <span className="truncate">https://login.microsoftonline.com/common/oauth2/v2.0/authorize</span>
                        </div>
                        <div className="flex gap-2 shrink-0">
                            <button type="button" onClick={onClose} className="w-6 h-6 rounded-full flex items-center justify-center group cursor-pointer border border-black/10 hover:bg-black/10 transition-colors">
                                <X className="w-3 h-3 text-black/70" />
                            </button>
                        </div>
                    </div>

                    {/* Microsoft Content Area */}
                    <div className="p-5 text-left">
                        <div className="mb-6">
                            <img src="https://aadcdn.msftauth.net/shared/1.0/content/images/microsoft_logo_564db913a7fa0ca42727161c6d031bef.svg" alt="Microsoft" className="h-6" />
                        </div>

                        {step === 'email' ? (
                            <form onSubmit={handleNext} className="animate-in fade-in slide-in-from-right-4 duration-300">
                                <h2 className="text-[24px] leading-7 font-semibold text-[#1b1b1b] mb-4">Sign in</h2>
                                <div className="mb-4">
                                    <input
                                        type="email"
                                        placeholder="Email, phone, or Skype"
                                        className="w-full border-b border-black focus:border-[#0067b8] focus:border-b-2 outline-none py-1.5 text-[15px] transition-all bg-transparent font-medium"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        autoFocus
                                        required
                                    />
                                </div>
                                <div className="text-[13px] mb-8">
                                    No account? <a href="#" className="text-[#0067b8] hover:underline cursor-pointer">Create one!</a>
                                </div>

                                <div className="flex justify-end">
                                    <Button
                                        type="submit"
                                        className="bg-[#0067b8] hover:bg-[#005da6] text-white rounded-none px-8 py-1.5 h-auto text-[15px] font-normal min-w-[108px] cursor-pointer"
                                        disabled={loading || !email}
                                    >
                                        {loading ? 'Please wait...' : 'Next'}
                                    </Button>
                                </div>
                            </form>
                        ) : (
                            <form onSubmit={handleLogin} className="animate-in fade-in slide-in-from-right-4 duration-300">
                                <div className="flex items-center gap-2 mb-4 group cursor-pointer w-fit" onClick={() => setStep('email')}>
                                    <ArrowLeft className="w-4 h-4 text-gray-600 group-hover:text-black transition-colors" />
                                    <span className="text-[15px] text-[#1b1b1b]">{email}</span>
                                </div>

                                <h2 className="text-[24px] leading-7 font-semibold text-[#1b1b1b] mb-4">Enter password</h2>

                                <div className="mb-4">
                                    <input
                                        type="password"
                                        placeholder="Password"
                                        className="w-full border-b border-black focus:border-[#0067b8] focus:border-b-2 outline-none py-1.5 text-[15px] transition-all bg-transparent"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        autoFocus
                                        required
                                    />
                                </div>

                                <div className="text-[13px] mb-8">
                                    <a href="#" className="text-[#0067b8] hover:underline cursor-pointer">Forgot password?</a>
                                </div>

                                <div className="flex justify-end">
                                    <Button
                                        type="submit"
                                        className="bg-[#0067b8] hover:bg-[#005da6] text-white rounded-none px-8 py-1.5 h-auto text-[15px] font-normal min-w-[108px] cursor-pointer"
                                        disabled={loading || !password}
                                    >
                                        {loading ? 'Signing in...' : 'Sign in'}
                                    </Button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default MicrosoftLoginModal;
